// Sheets the app hands out: the blank template, a product export (same
// columns, so it can be edited in Excel and imported back) and the rows that
// weren't imported, with the reason in an extra column.
import type { JsonValue, ProductRow, RowOf } from '@ims/data'
import type { AttributeField, BusinessType, GroupId } from '../catalog.ts'
import { presetFor } from '../units.ts'
import { readValues } from '../variants.ts'
import type { ImportPlan } from './validate.ts'

export type SheetCell = string | number
export type SheetRows = SheetCell[][]

export const TEMPLATE_HEADERS = [
  'SKU',
  'Name',
  'Barcode',
  'Category',
  'Unit',
  'Selling price',
  'Cost price',
  'VAT %',
  'Price includes VAT',
  'Opening stock',
  'Low-stock alert',
  'Description',
  'Brand',
  'Manufacturer',
  'MRP',
  'HS code',
  'Shelf / rack',
  'Tags',
  'Variant of',
  'Variant',
  'Wholesale price',
  'Minimum price',
  'Scale item code (PLU)',
] as const

/** A column of the template: when it is shown, and what the "How to fill" sheet says about it. */
export interface TemplateColumn {
  header: string
  /** Shown only to a business with this field group on (none: always shown). */
  group?: GroupId
  required?: boolean
  help: string
}

/**
 * Every column a template can have, in order. The headings are ones the importer
 * maps by itself (a test checks it), so a template with fewer columns imports the
 * same way as one with all of them.
 */
export const TEMPLATE_COLUMNS: TemplateColumn[] = [
  {
    header: 'SKU',
    help: 'Your own code for the product. Leave it empty and one is made from the name.',
  },
  { header: 'Name', required: true, help: 'What the product is called on bills.' },
  { header: 'Barcode', help: 'The number under the barcode on the pack, if it has one.' },
  {
    header: 'Category',
    help: 'A group such as Noodles or Tools. A new one is offered for you to add.',
  },
  { header: 'Unit', help: 'How it is sold: pcs, kg, l, box… A new one is offered for you to add.' },
  { header: 'Selling price', required: true, help: 'The price for one unit, in rupees.' },
  {
    header: 'Cost price',
    help: 'What one unit costs you. Used for profit; never shown to customers.',
  },
  { header: 'VAT %', help: '13, or 0 for exempt. Leave it empty to use your default.' },
  {
    header: 'Price includes VAT',
    help: 'Yes if the selling price already has VAT in it, No if VAT is added on top.',
  },
  { header: 'Opening stock', help: 'How many you have now. Used for new products only.' },
  { header: 'Low-stock alert', help: 'Warn when stock falls to this number.' },
  { header: 'Description', help: 'Any note about the product.' },
  { header: 'Brand', group: 'brand', help: 'The brand name. A new one is added for you.' },
  { header: 'Manufacturer', group: 'brand', help: 'Who makes it.' },
  {
    header: 'MRP',
    group: 'pricing',
    help: 'The maximum retail price printed on the pack, VAT included.',
  },
  { header: 'HS code', group: 'trade', help: 'The customs code, if you use one.' },
  { header: 'Shelf / rack', group: 'shelf', help: 'Where it is kept, such as A-3.' },
  { header: 'Tags', group: 'shelf', help: 'Search words, separated by commas.' },
  {
    header: 'Variant of',
    group: 'variants',
    help: 'For a size or colour of a product: the main product’s SKU. Leave empty otherwise.',
  },
  { header: 'Variant', group: 'variants', help: 'Which one it is, like “Size: M” or “M / Red”.' },
  { header: 'Wholesale price', group: 'wholesale', help: 'The price for wholesale customers.' },
  { header: 'Minimum price', group: 'wholesale', help: 'Staff cannot sell one unit below this.' },
  {
    header: 'Scale item code (PLU)',
    group: 'scale',
    help: 'The item number your weighing scale prints in its barcode.',
  },
  {
    header: 'Batch no.',
    group: 'batches',
    help: 'New products only: the batch the opening stock belongs to.',
  },
  {
    header: 'Mfg date',
    group: 'batches',
    help: 'Manufacture date of that batch, like 2026-01-31.',
  },
  { header: 'Expiry date', group: 'batches', help: 'Expiry date of that batch, like 2027-12-31.' },
]

/** The columns for a business: the ones everyone has, plus those of its field groups. */
export function templateColumns(groups: ReadonlySet<GroupId> | 'all'): TemplateColumn[] {
  return TEMPLATE_COLUMNS.filter((c) => !c.group || groups === 'all' || groups.has(c.group))
}

/** An example product: a cell per heading (a heading the business does not have is left out). */
type Example = Record<string, SheetCell>

// Two or three rows a shop of each kind recognises. Units are named by their usual
// symbol and swapped for the business's own; VAT comes from the business's settings.
const EXAMPLES: Record<NonNullable<BusinessType>, Example[]> = {
  kirana: [
    {
      SKU: 'WW-75',
      Name: 'Wai Wai Noodles 75g',
      Barcode: '8901234500011',
      Category: 'Noodles',
      Unit: 'pcs',
      'Selling price': 25,
      'Cost price': 18,
      'Opening stock': 120,
      'Low-stock alert': 20,
      Brand: 'CG Foods',
      Manufacturer: 'Chaudhary Group',
      MRP: 25,
      'HS code': '1902.30',
      'Shelf / rack': 'A-1',
      Tags: 'snacks',
    },
    {
      SKU: 'RICE-KG',
      Name: 'Basmati Rice (loose)',
      Category: 'Grocery',
      Unit: 'kg',
      'Selling price': 160,
      'Cost price': 130,
      'Opening stock': 50.5,
      'Low-stock alert': 10,
      'Shelf / rack': 'B-2',
    },
  ],
  pharmacy: [
    {
      SKU: 'PCM-500',
      Name: 'Paracetamol 500mg (strip of 10)',
      Barcode: '8901234500103',
      Category: 'Tablets',
      Unit: 'strip',
      'Selling price': 20,
      'Cost price': 14,
      'Opening stock': 200,
      'Low-stock alert': 30,
      Brand: 'Cetamol',
      Manufacturer: 'Deurali-Janta',
      MRP: 20,
      'Shelf / rack': 'T-2',
      'Batch no.': 'PC2401',
      'Mfg date': '2026-01-15',
      'Expiry date': '2028-01-14',
    },
    {
      SKU: 'ORS-21',
      Name: 'ORS Sachet 21g',
      Category: 'General',
      Unit: 'pcs',
      'Selling price': 15,
      'Cost price': 10,
      'Opening stock': 80,
      'Low-stock alert': 20,
      Brand: 'Jeevan Jal',
      MRP: 15,
      'Shelf / rack': 'G-1',
      'Batch no.': 'OR2409',
      'Mfg date': '2026-03-01',
      'Expiry date': '2028-02-28',
    },
  ],
  hardware: [
    {
      SKU: 'CEM-50',
      Name: 'OPC Cement 50kg',
      Category: 'Cement',
      Unit: 'bag',
      'Selling price': 850,
      'Cost price': 780,
      'Opening stock': 60,
      'Low-stock alert': 10,
      Brand: 'Shivam',
      'HS code': '2523.29',
      'Shelf / rack': 'Yard',
      'Wholesale price': 820,
    },
    {
      SKU: 'PVC-20',
      Name: 'PVC Pipe 20mm',
      Category: 'Pipes',
      Unit: 'm',
      'Selling price': 95,
      'Cost price': 70,
      'Opening stock': 150.5,
      'Low-stock alert': 30,
      Brand: 'Panchakanya',
      'Shelf / rack': 'R-4',
      'Wholesale price': 88,
    },
  ],
  apparel: [
    {
      SKU: 'TS-BLU-M',
      Name: 'Cotton T-Shirt',
      Category: 'T-Shirts',
      Unit: 'pcs',
      'Selling price': 800,
      'Cost price': 450,
      'Opening stock': 12,
      'Low-stock alert': 3,
      Brand: 'Himal Wear',
      'Variant of': 'TS-BLU',
      Variant: 'Size: M',
    },
    {
      SKU: 'TS-BLU-L',
      Name: 'Cotton T-Shirt',
      Category: 'T-Shirts',
      Unit: 'pcs',
      'Selling price': 800,
      'Cost price': 450,
      'Opening stock': 8,
      'Low-stock alert': 3,
      Brand: 'Himal Wear',
      'Variant of': 'TS-BLU',
      Variant: 'Size: L',
    },
    {
      SKU: 'SOCK-3',
      Name: 'Ankle Socks (pack of 3)',
      Category: 'Socks',
      Unit: 'pair',
      'Selling price': 350,
      'Cost price': 200,
      'Opening stock': 40,
      'Low-stock alert': 10,
    },
  ],
  electronics: [
    {
      SKU: 'CHG-20W',
      Name: 'Fast Charger 20W',
      Barcode: '8901234500202',
      Category: 'Chargers',
      Unit: 'pcs',
      'Selling price': 1200,
      'Cost price': 800,
      'Opening stock': 25,
      'Low-stock alert': 5,
      Brand: 'Voltix',
      MRP: 1500,
    },
    {
      SKU: 'EAR-BT1',
      Name: 'Bluetooth Earbuds',
      Category: 'Audio',
      Unit: 'box',
      'Selling price': 2500,
      'Cost price': 1700,
      'Opening stock': 10,
      'Low-stock alert': 2,
      Brand: 'SoundOne',
      MRP: 2999,
    },
  ],
  cosmetics: [
    {
      SKU: 'FW-100',
      Name: 'Face Wash 100ml',
      Barcode: '8901234500301',
      Category: 'Skin care',
      Unit: 'tube',
      'Selling price': 320,
      'Cost price': 240,
      'Opening stock': 30,
      'Low-stock alert': 6,
      Brand: 'Glow',
      MRP: 320,
      'Batch no.': 'FW2405',
      'Mfg date': '2026-02-01',
      'Expiry date': '2028-01-31',
    },
    {
      SKU: 'LIP-RD',
      Name: 'Lipstick Red',
      Category: 'Make-up',
      Unit: 'pcs',
      'Selling price': 450,
      'Cost price': 300,
      'Opening stock': 18,
      'Low-stock alert': 4,
      Brand: 'Rang',
      MRP: 499,
    },
  ],
  bakery: [
    {
      SKU: 'BRD-400',
      Name: 'Milk Bread 400g',
      Category: 'Bread',
      Unit: 'pack',
      'Selling price': 70,
      'Cost price': 45,
      'Opening stock': 30,
      'Low-stock alert': 8,
      'Batch no.': 'B-1007',
      'Mfg date': '2026-10-07',
      'Expiry date': '2026-10-11',
    },
    {
      SKU: 'LADDU',
      Name: 'Besan Laddu (loose)',
      Category: 'Sweets',
      Unit: 'kg',
      'Selling price': 900,
      'Cost price': 600,
      'Opening stock': 6.5,
      'Low-stock alert': 2,
      'Scale item code (PLU)': '104',
    },
  ],
  restaurant: [
    {
      SKU: 'MOMO-C',
      Name: 'Chicken Momo',
      Category: 'Momo',
      Unit: 'plate',
      'Selling price': 220,
      'Cost price': 110,
    },
    {
      SKU: 'COKE-250',
      Name: 'Coke 250ml',
      Category: 'Drinks',
      Unit: 'bottle',
      'Selling price': 80,
      'Cost price': 55,
      'Opening stock': 48,
      'Low-stock alert': 12,
      Brand: 'Coca-Cola',
    },
  ],
  workshop: [
    {
      SKU: 'BP-PLS',
      Name: 'Brake Pad Set (Pulsar 150)',
      Category: 'Brakes',
      Unit: 'set',
      'Selling price': 650,
      'Cost price': 480,
      'Opening stock': 8,
      'Low-stock alert': 2,
      Brand: 'Bajaj',
      'Shelf / rack': 'R-2',
    },
    {
      SKU: 'OIL-20W40',
      Name: 'Engine Oil 20W-40 (1 litre)',
      Barcode: '8901234500400',
      Category: 'Oils',
      Unit: 'l',
      'Selling price': 720,
      'Cost price': 600,
      'Opening stock': 24,
      'Low-stock alert': 6,
      Brand: 'Servo',
      'Shelf / rack': 'O-1',
    },
  ],
  wholesale: [
    {
      SKU: 'WW-CTN',
      Name: 'Wai Wai Noodles (carton of 30)',
      Barcode: '8901234500509',
      Category: 'Noodles',
      Unit: 'carton',
      'Selling price': 720,
      'Cost price': 640,
      'Opening stock': 40,
      'Low-stock alert': 8,
      Brand: 'CG Foods',
      MRP: 750,
      'HS code': '1902.30',
      'Wholesale price': 690,
      'Minimum price': 670,
    },
    {
      SKU: 'OIL-15',
      Name: 'Sunflower Oil 15L tin',
      Category: 'Oil',
      Unit: 'box',
      'Selling price': 4200,
      'Cost price': 3900,
      'Opening stock': 15,
      'Low-stock alert': 3,
      Brand: 'Dhara',
      'Wholesale price': 4100,
      'Minimum price': 4000,
    },
  ],
  general: [
    {
      SKU: 'NB-200',
      Name: 'Notebook 200 pages',
      Barcode: '8901234500608',
      Category: 'Stationery',
      Unit: 'pcs',
      'Selling price': 120,
      'Cost price': 85,
      'Opening stock': 60,
      'Low-stock alert': 12,
      Brand: 'Nebico',
      'Shelf / rack': 'S-1',
    },
    {
      SKU: 'PEN-BL',
      Name: 'Ball Pen Blue (box of 10)',
      Category: 'Stationery',
      Unit: 'box',
      'Selling price': 100,
      'Cost price': 70,
      'Opening stock': 25,
      'Low-stock alert': 5,
    },
  ],
}

export interface TemplateOptions {
  /** Whose examples to show (none: a grocery's). */
  businessType?: BusinessType | null
  /** The business's field groups, or every column there is. */
  groups?: ReadonlySet<GroupId> | 'all'
  /** The business's extra product fields (medicine details…), added as columns at the end. */
  attributeFields?: readonly AttributeField[]
  /** The business's unit symbols: an example's unit is written the way the business writes it. */
  unitSymbols?: readonly string[]
  /** What the examples say about VAT: the business's default rate (null: none) and its price habit. */
  vatPercent?: number | null
  pricesIncludeVat?: boolean
}

/** An example's unit as the business has it ("ltr" for our "l"), or nothing when it has none like it. */
function unitFor(symbol: SheetCell | undefined, known: readonly string[] | undefined): SheetCell {
  if (typeof symbol !== 'string' || !known) return symbol ?? ''
  const mine = known.find((u) => u.trim().toLowerCase() === symbol)
  if (mine) return mine
  const preset = presetFor(symbol)
  return known.find((u) => presetFor(u)?.symbol === preset?.symbol && preset) ?? ''
}

/**
 * The "Products" sheet of a template: the business's columns and a few example
 * rows of its own kind of goods. Called without options it is every column with a
 * grocery's examples (what the app handed out before templates followed the business).
 */
export function templateRows(
  attributeFieldsOrOptions: readonly AttributeField[] | TemplateOptions = {},
): SheetRows {
  const options: TemplateOptions = Array.isArray(attributeFieldsOrOptions)
    ? { attributeFields: attributeFieldsOrOptions as readonly AttributeField[] }
    : (attributeFieldsOrOptions as TemplateOptions)
  const columns = templateColumns(options.groups ?? 'all')
  const extra = options.attributeFields ?? []
  const vat = options.vatPercent === undefined ? 13 : (options.vatPercent ?? '')
  const includes = (options.pricesIncludeVat ?? true) ? 'Yes' : 'No'
  const examples = EXAMPLES[options.businessType ?? 'kirana'] ?? EXAMPLES.kirana
  return [
    [...columns.map((c) => c.header), ...extra.map((f) => f.label)],
    ...examples.map((example) => [
      ...columns.map(({ header }): SheetCell => {
        if (header === 'Unit') return unitFor(example.Unit, options.unitSymbols)
        if (header === 'VAT %') return vat
        if (header === 'Price includes VAT') return includes
        return example[header] ?? ''
      }),
      ...extra.map(() => ''),
    ]),
  ]
}

/** The second sheet: what each column means, read by someone with no one to ask. */
export function templateGuideRows(options: TemplateOptions = {}): SheetRows {
  const [, first] = templateRows(options)
  const columns = templateColumns(options.groups ?? 'all')
  return [
    ['How to fill the Products sheet'],
    ['One product per row. Delete the example rows before you import the file.'],
    ['Only Name and Selling price are needed. Leave any other cell empty if you do not know it.'],
    ['You can remove a column you do not use, or add your own: unknown columns are ignored.'],
    [],
    ['Column', 'Needed?', 'What to put', 'Example'],
    ...columns.map((c, i) => [c.header, c.required ? 'Yes' : 'Optional', c.help, first?.[i] ?? '']),
    ...(options.attributeFields ?? []).map((f) => [
      f.label,
      'Optional',
      'One of your own product fields.',
      '',
    ]),
  ]
}

const rupees = (paisa: number | null | undefined) => (paisa == null ? '' : paisa / 100)
const units = (milli: number | null | undefined) => (milli == null ? '' : milli / 1000)

function attributeCell(field: AttributeField, value: JsonValue | undefined): SheetCell {
  if (value === undefined || value === null) return ''
  if (field.type === 'yesno') return value ? 'Yes' : 'No'
  return typeof value === 'number' ? value : String(value)
}

export function productsToRows(
  products: ProductRow[],
  lookups: {
    units: RowOf<'units'>[]
    taxRates: RowOf<'tax_rates'>[]
    categories: RowOf<'categories'>[]
    brands?: RowOf<'brands'>[]
  },
  stockByProduct: ReadonlyMap<string, number>,
  attributeFields: readonly AttributeField[] = [],
): SheetRows {
  const unit = new Map(lookups.units.map((u) => [u.id, u.symbol]))
  const rate = new Map(lookups.taxRates.map((t) => [t.id, t.rate_bp / 100]))
  const category = new Map(lookups.categories.map((c) => [c.id, c.name]))
  const brand = new Map((lookups.brands ?? []).map((b) => [b.id, b.name]))
  const skuById = new Map(products.map((p) => [p.id, p.sku]))
  // "In stock", not "Opening stock": on re-import it only seeds new products.
  const header = [
    ...TEMPLATE_HEADERS.map((h) => (h === 'Opening stock' ? 'In stock' : h)),
    ...attributeFields.map((f) => f.label),
  ]
  return [
    header,
    ...products.map((p) => {
      const attrs =
        p.attributes && typeof p.attributes === 'object' && !Array.isArray(p.attributes)
          ? p.attributes
          : {}
      return [
        p.sku,
        p.name,
        p.barcode ?? '',
        (p.category_id && category.get(p.category_id)) || '',
        (p.unit_id && unit.get(p.unit_id)) || '',
        rupees(p.sell_price_paisa),
        rupees(p.cost_price_paisa),
        p.tax_rate_id ? (rate.get(p.tax_rate_id) ?? '') : '',
        p.price_includes_tax ? 'Yes' : 'No',
        p.track_stock ? units(stockByProduct.get(p.id) ?? 0) : '',
        p.min_stock_milli ? units(p.min_stock_milli) : '',
        p.description ?? '',
        (p.brand_id && brand.get(p.brand_id)) || '',
        p.manufacturer ?? '',
        rupees(p.mrp_paisa),
        p.hs_code ?? '',
        p.shelf_location ?? '',
        p.tags ?? '',
        (p.parent_id && skuById.get(p.parent_id)) || '',
        Object.entries(readValues(p.variant_values))
          .map(([k, v]) => `${k}: ${v}`)
          .join(', '),
        rupees(p.wholesale_price_paisa),
        rupees(p.min_price_paisa),
        p.plu_code ?? '',
        ...attributeFields.map((f) => attributeCell(f, attrs[f.key])),
      ]
    }),
  ]
}

/** Rows the import leaves out and why: errors, and new products over the plan limit. */
export function notImported(plan: ImportPlan): { index: number; reason: string }[] {
  return plan.rows.flatMap((r) =>
    r.status === 'error'
      ? [{ index: r.index, reason: r.errors.map((e) => e.message).join('; ') }]
      : r.overLimit
        ? [{ index: r.index, reason: 'Over the plan limit' }]
        : [],
  )
}

/**
 * The header row plus every row that wasn't imported, as it was in the file,
 * with the reason in an extra column: fix or upgrade, then import this file.
 */
export function notImportedRows(
  headers: string[],
  dataRows: string[][],
  plan: ImportPlan,
): SheetRows {
  const out: SheetRows = [[...headers, 'Not imported because']]
  for (const { index, reason } of notImported(plan)) {
    const original = dataRows[index] ?? []
    out.push([...headers.map((_, i) => original[i] ?? ''), reason])
  }
  return out
}
